import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import Breadcrumbs from "../../components/Breadcrumbs";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Modal } from "antd";
import { post } from "../../services/ApiService";

const statuses = ["Open", "Closed"];

const ComplaintsList = () => {
  const [complaints, setComplaints] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalOpen, setTotalOpen] = useState(0);
  const [totalClosed, setTotalClosed] = useState(0);
  const [filters, setFilters] = useState({ search: "", status: "All" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    date: "",
    status: "Open",
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await post("api/complaints/list", {
        page: 1,
        limit: 100,
        search: filters.search,
        status: filters.status,
      });
      if (res.success) {
        setComplaints(res.complaints);
        setTotal(res.total);
        setTotalOpen(res.totalOpen);
        setTotalClosed(res.totalClosed);
      } else {
        setComplaints([]);
      }
    } catch (error) {
      console.error(error.message);
      setComplaints([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const openAddModal = () => {
    setForm({ title: "", description: "", date: new Date().toISOString().split("T")[0], status: "Open" });
    setIsModalOpen(true);
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.description.trim()) { toast.error("Description is required"); return; }
    if (!form.date) { toast.error("Date is required"); return; }

    try {
      setIsSubmitting(true);
      const res = await post("api/complaints/add", {
        title: form.title,
        description: form.description,
        date: form.date,
        status: form.status,
      });
      if (res.success) {
        toast.success("Complaint added successfully");
        setIsModalOpen(false);
        fetchData();
      } else {
        toast.error(res.error || "Failed to add complaint");
      }
    } catch (error) {
      toast.error("Failed to add complaint");
    } finally {
      setIsSubmitting(false);
    }
  };

  const changeStatus = async (id, newStatus) => {
    try {
      const res = await post(`api/complaints/status/${id}`, { status: newStatus });
      if (res.success) {
        toast.success(`Status changed to ${newStatus}`);
        fetchData();
      } else {
        toast.error(res.error || "Failed to change status");
      }
    } catch (error) {
      toast.error("Failed to change status");
    }
  };

  const getStatusBadge = (status) => {
    const map = { Open: "bg-success", Closed: "bg-danger" };
    return map[status] || "bg-secondary";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <>
      <Layout>
        <Toaster position="top-center" reverseOrder={false} />
        <div className="row mb-9 align-items-center justify-content-between">
          <div className="col-sm-6 mb-8 mb-sm-0">
            <h2 className="fs-4 mb-0">Complaints</h2>
            <Breadcrumbs />
          </div>
          <div className="col-sm-6 col-md-3 d-flex flex-wrap justify-content-sm-end">
            <button className="btn btn-primary btn-sm" onClick={openAddModal}>
              <i className="fas fa-plus me-2" />
              Add Complaint
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="row mb-6">
          <div className="col-lg-4 col-md-4 col-4 mb-4">
            <div className="card rounded-4 p-5 text-center border-0 shadow-sm">
              <h3 className="mb-1">{total}</h3>
              <small className="text-muted">Total</small>
            </div>
          </div>
          <div className="col-lg-4 col-md-4 col-4 mb-4">
            <div className="card rounded-4 p-5 text-center border-0 shadow-sm">
              <h3 className="mb-1 text-success">{totalOpen}</h3>
              <small className="text-muted">Open</small>
            </div>
          </div>
          <div className="col-lg-4 col-md-4 col-4 mb-4">
            <div className="card rounded-4 p-5 text-center border-0 shadow-sm">
              <h3 className="mb-1 text-danger">{totalClosed}</h3>
              <small className="text-muted">Closed</small>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-4 rounded-4 p-7">
          <div className="card-header bg-transparent px-0 pt-0 pb-7">
            <div className="row align-items-center justify-content-between mb-4">
              <div className="col-lg-4 col-md-6 col-6 mb-md-0 mb-6">
                <input
                  type="text"
                  placeholder="Search by ID or title..."
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="form-control bg-input border-0"
                />
              </div>
              <div className="col-lg-3 col-md-6 col-6 mb-md-0 mb-6">
                <select className="form-select" name="status" value={filters.status} onChange={handleFilterChange}>
                  <option value="All">All Status</option>
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card-body px-0 pt-7 pb-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle table-nowrap mb-0 table-borderless" style={{ minWidth: "800px" }}>
                <thead className="table-light">
                  <tr>
                    <th className="align-middle" scope="col">#ID</th>
                    <th className="align-middle" scope="col">Title</th>
                    <th className="align-middle" scope="col">Date</th>
                    <th className="align-middle" scope="col">Status</th>
                    <th className="align-middle" scope="col">Replies</th>
                    <th className="align-middle text-center" scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8">
                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                      </td>
                    </tr>
                  ) : complaints.length > 0 ? (
                    complaints.map((item) => (
                      <tr key={item._id}>
                        <td>
                          <Link to={`/complaints/${item._id}`} className="fw-semibold text-primary">
                            {item.complaintId}
                          </Link>
                        </td>
                        <td className="text-body-emphasis" style={{ maxWidth: "300px", whiteSpace: "normal" }}>
                          {item.title}
                        </td>
                        <td>{formatDate(item.date)}</td>
                        <td>
                          <span className={`badge ${getStatusBadge(item.status)}`} style={{ fontSize: "11px" }}>
                            {item.status}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-body-secondary text-dark">{item.replies.length}</span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex flex-nowrap justify-content-center align-items-center">
                            <Link to={`/complaints/${item._id}`} className="btn btn-primary py-4 fs-13px btn-xs me-2" title="View Details">
                              <i className="fa fa-eye"></i>
                            </Link>
                            <select
                              className="form-select form-select-sm"
                              style={{ width: "130px", fontSize: "12px", outline: "none", boxShadow: "none" }}
                              value={item.status}
                              onChange={(e) => changeStatus(item._id, e.target.value)}
                            >
                              {statuses.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-8">
                        <img src="/assets/images/no-data.png" style={{ width: "60px", opacity: "0.5" }} alt="" />
                        <h6 style={{ color: "#918f8f", fontFamily: "math", fontWeight: "500" }}>No Complaints Found</h6>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add Modal */}
        <Modal
          open={isModalOpen}
          closable={true}
          footer={null}
          onCancel={() => setIsModalOpen(false)}
          width={600}
          title="Add New Complaint"
        >
          <div className="row mt-4">
            <div className="col-12 mb-3">
              <label className="form-label fw-semibold">Title <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                name="title"
                value={form.title}
                onChange={handleFormChange}
                placeholder="Enter complaint title"
              />
            </div>
            <div className="col-12 mb-3">
              <label className="form-label fw-semibold">Description <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                name="description"
                value={form.description}
                onChange={handleFormChange}
                rows={4}
                placeholder="Describe the complaint in detail..."
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className="form-control"
                name="date"
                value={form.date}
                onChange={handleFormChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Status</label>
              <select className="form-select" name="status" value={form.status} onChange={handleFormChange}>
                {statuses.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-12 mt-3 d-flex justify-content-end gap-2">
              <button className="btn btn-sm btn-secondary rounded-pill px-4" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="btn btn-sm btn-primary rounded-pill px-4" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </Modal>
      </Layout>
    </>
  );
};

export default ComplaintsList;
